import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getRelatedIssuers,
  saveRelatedIssuersBatch,
} from '../../api/relatedIssuers';
import { useFunds } from '../../hooks/useFunds';
import { useTrustees } from '../../hooks/fundLookups';
import type { RelatedIssuer } from '../../types/RelatedIssuer';
import { dateColumnProps } from '../../utils/dateColumn';
import { t } from '../../i18n/t';

// "מנפיקים קשורים" (RelatedIssuers, TENANT). Related-party issuers per
// trustee, over a validity period, feeding the prospectus report (legacy
// ManpikKashur). issuerId + fromDate are the COMPOSITE PK — editable only
// while the row is `__status === 'added'` (mirrors CapitalConsolidationsPage's
// editableOnAdd rule). issuerId itself stays PLAIN numeric (no dropdown) —
// Central ISSUERS has 546 rows, same call as MutualFunds.issuerId /
// AssetsPage precedent. trusteeId IS a dropdown — Trustees is a small Central
// lookup, and -1 ("מנהל הקרן") is a real row that must appear as an option,
// not be treated as a null sentinel.
const editableOnAdd = (
  params: EditableCallbackParams<RelatedIssuer>,
) => (params.data as { __status?: string } | undefined)?.__status === 'added';

const newRelatedIssuerDefaults = (): Partial<RelatedIssuer> => ({
  issuerId: 0,
  trusteeId: 0,
  fromDate: null as unknown as string,
  toDate: null,
});

export function RelatedIssuersPage() {
  // Tenant-scoped gate — see module comment on CapitalConsolidationsPage /
  // CreationRedemptionsPage: useFunds calls setTenantId itself before its
  // fetch, fixing the hard-refresh X-Tenant-Id race for THIS page's own
  // getRelatedIssuers() call (RelatedIssuers itself isn't otherwise related
  // to funds). Only used for its loading/error signal here.
  const { loading: fundsLoading, error: fundsError } = useFunds();
  // Trustees is a small CENTRAL lookup (not tenant-scoped) feeding the
  // trusteeId dropdown.
  const trustees = useTrustees();

  const columnDefs = useMemo<ColDef<RelatedIssuer>[]>(() => {
    if (!trustees.data) return [];

    // trusteeId is REQUIRED (NOT NULL) — no "none" option. -1 is a real row
    // ("מנהל הקרן"), keyed by a unique "<id> - <name>" label (mirrors
    // CapitalConsolidationsPage's required fundId dropdown); orphan ids fall
    // back to the raw id so dirty data stays visible/editable.
    const trusteeLabelById = new Map<number, string>(
      trustees.data.map((tr) => [tr.trusteeId, `${tr.trusteeId} - ${tr.trusteeName}`]),
    );
    const trusteeIdByLabel = new Map<string, number>(
      trustees.data.map((tr) => [`${tr.trusteeId} - ${tr.trusteeName}`, tr.trusteeId]),
    );
    const trusteeLabels = Array.from(trusteeIdByLabel.keys());

    return [
      {
        field: 'issuerId',
        headerName: t('relatedIssuers.columns.issuerId'),
        width: 130,
        editable: editableOnAdd,
        cellDataType: 'number',
        sort: 'asc',
      },
      {
        field: 'trusteeId',
        headerName: t('relatedIssuers.columns.trusteeId'),
        width: 260,
        editable: true,
        // cellDataType: 'text' is REQUIRED on id<->name dropdowns — without it
        // AgGrid infers number from the field type and coerces the picked
        // label back to NaN before the valueSetter runs.
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: trusteeLabels },
        valueGetter: (p: ValueGetterParams<RelatedIssuer>) => {
          if (!p.data) return '';
          return trusteeLabelById.get(p.data.trusteeId) ?? String(p.data.trusteeId);
        },
        valueSetter: (p: ValueSetterParams<RelatedIssuer>) => {
          if (!p.data) return false;
          const id = trusteeIdByLabel.get(p.newValue as string);
          if (id === undefined) return false;
          p.data.trusteeId = id;
          return true;
        },
      },
      {
        field: 'fromDate',
        headerName: t('relatedIssuers.columns.fromDate'),
        width: 150,
        editable: editableOnAdd,
        ...dateColumnProps<RelatedIssuer>('fromDate'),
      },
      {
        field: 'toDate',
        headerName: t('relatedIssuers.columns.toDate'),
        width: 150,
        editable: true,
        ...dateColumnProps<RelatedIssuer>('toDate'),
      },
    ];
  }, [trustees.data]);

  if (fundsError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('relatedIssuers.errorPrefix', { message: fundsError })}
        </div>
      </div>
    );
  }

  if (fundsLoading || !trustees.data) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<RelatedIssuer>
      resourceCode="relatedIssuers"
      titleKey="relatedIssuers.pageTitle"
      idField="idKey"
      getRows={getRelatedIssuers}
      saveBatch={(added, modified, deletedIdKeys) =>
        saveRelatedIssuersBatch(added, modified, deletedIdKeys as string[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newRelatedIssuerDefaults}
    />
  );
}
